import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getPodConfiguration } from 'services/webExtension';
import { IPodFeatures, PodFeature } from 'services/webExtension/types';
import { RootState } from 'store/index';

const ALL_FEATURES_ENABLED: IPodFeatures = {
  secureDataSharing: true,
  technicianVerification: true,
  quickTickets: true,
  approvalRequest: true,
  alpha: false,
  alphaVoice: false,
};

interface IInitialState {
  features: IPodFeatures | null;
  loading: boolean;
}

const initialState: IInitialState = {
  features: null,
  loading: false,
};

export const ThunkGetPodFeatures = createAsyncThunk<IPodFeatures>(
  'podFeatures/get',
  async () => {
    try {
      const { status, data } = await getPodConfiguration();
      const features = status >= 200 && status < 300 ? data?.features : null;
      return features ? { ...ALL_FEATURES_ENABLED, ...features } : ALL_FEATURES_ENABLED;
    } catch {
      return ALL_FEATURES_ENABLED;
    }
  },
  { condition: (_, { getState }) => !(getState() as RootState).podFeatures.loading },
);

const podFeatures = createSlice({
  name: 'podFeatures',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(ThunkGetPodFeatures.pending, state => {
        state.loading = true;
      })
      .addCase(ThunkGetPodFeatures.fulfilled, (state, action) => {
        state.loading = false;
        state.features = action.payload;
      })
      .addCase(ThunkGetPodFeatures.rejected, state => {
        state.loading = false;
        state.features = ALL_FEATURES_ENABLED;
      });
  },
});

export default podFeatures.reducer;

export const PodFeaturesSelector = (state: RootState): IPodFeatures | null =>
  state.podFeatures.features;

export const PodFeaturesLoadingSelector = (state: RootState): boolean => state.podFeatures.loading;

export const isPodFeatureEnabledSelector =
  (feature: PodFeature) =>
  (state: RootState): boolean =>
    state.podFeatures?.features?.[feature] === true;
